import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import { BaseLayout } from '@/shared/components/layout';
import { useDrones } from '../hooks/useDrones';
import { Card, CardContent } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/custom/tabs';
import { Box, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
// Importing for the side effect: ``registerTour`` runs at module load.
import { DRONES_TOUR_ID } from '@/features/tours/drones';
import { useTour } from '@/features/tours';
import { FlightPathPanel } from './FlightPathPanel';
import { DroneUploadDialog } from './DroneUploadDialog';

const DroneViewer: React.FC = () => {
  const { projectId } = useCurrentProject();
  const { data: dronesData, isLoading, error } = useDrones(projectId);
  
  const [selectedDroneType, setSelectedDroneType] = useState<string>('');
  const [selectedDroneName, setSelectedDroneName] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [iframeUrl, setIframeUrl] = useState<string>('https://www.ironview360.com/static/animations/Loading/#media=loading-screen');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);
  const [currentFlightTime, setCurrentFlightTime] = useState(0);
  const viewerFrameRef = useRef<HTMLIFrameElement>(null);

  const selectedVideo = useMemo(() => {
    if (!dronesData || !selectedDroneType || !selectedDroneName || !selectedDate) return undefined;
    return dronesData.project_drones[selectedDroneType]?.[selectedDroneName]?.videos.find((video) => video.date === selectedDate);
  }, [dronesData, selectedDate, selectedDroneName, selectedDroneType]);

  // Auto-start the drones guided tour the first time a user lands on this
  // route. Gated on at least one drone tour actually being available so
  // the tour doesn't try to spotlight an empty sidebar.
  const { hydrated: tourHydrated, active: activeTour, startTour } = useTour();
  const hasDroneVideo = useMemo(() => {
    if (!dronesData) return false;
    for (const droneType of dronesData.drone_types) {
      const drones = dronesData.project_drones[droneType];
      if (!drones) continue;
      for (const droneName of Object.keys(drones)) {
        if (drones[droneName].dates.length > 0) return true;
      }
    }
    return false;
  }, [dronesData]);
  useEffect(() => {
    if (!tourHydrated) return;
    // Don't re-enter ``startTour`` while a tour is already running.
    // ``startTour``'s identity changes on every step (optimistic cache
    // update) which would otherwise re-fire this effect mid-tour.
    if (activeTour) return;
    if (!hasDroneVideo) return;
    void startTour(DRONES_TOUR_ID);
  }, [tourHydrated, activeTour, startTour, hasDroneVideo]);

  // Set default selections when data loads
  useEffect(() => {
    if (dronesData && dronesData.drone_types.length > 0) {
      const firstType = dronesData.drone_types[0];
      setSelectedDroneType(firstType);
      
      const firstDroneName = Object.keys(dronesData.project_drones[firstType])[0];
      if (firstDroneName) {
        setSelectedDroneName(firstDroneName);
        const firstDate = dronesData.project_drones[firstType][firstDroneName]?.dates[0];
        if (firstDate) {
          setSelectedDate(firstDate);
          const videoInfo = dronesData.project_drones[firstType][firstDroneName].videos.find(v => v.date === firstDate);
          if (videoInfo) setIframeUrl(videoInfo.index_url);
        }
      }
    }
  }, [dronesData]);

  const handleDateClick = (droneName: string, date: string, droneType: string) => {
    if (!dronesData) return;
    const droneData = dronesData.project_drones[droneType]?.[droneName];
    if (!droneData) return;
    const videoInfo = droneData.videos.find(v => v.date === date);
    if (!videoInfo) return;
    setSelectedDroneName(droneName);
    setSelectedDate(date);
    setIframeUrl(videoInfo.index_url);
    setCurrentFlightTime(0);
  };

  useEffect(() => {
    const handleFlightMessage = (event: MessageEvent) => {
      if (!viewerFrameRef.current?.contentWindow || event.source !== viewerFrameRef.current.contentWindow) return;
      if (event.origin !== new URL(iframeUrl).origin || event.data?.type !== 'ironview-flight:time') return;
      if (typeof event.data.currentTime === 'number') setCurrentFlightTime(event.data.currentTime);
    };
    window.addEventListener('message', handleFlightMessage);
    return () => window.removeEventListener('message', handleFlightMessage);
  }, [iframeUrl]);

  const seekFlightPath = (time: number) => {
    const frameWindow = viewerFrameRef.current?.contentWindow;
    if (!frameWindow) return;
    frameWindow.postMessage({ type: 'ironview-flight:seek', time, play: true }, new URL(iframeUrl).origin);
    setCurrentFlightTime(time);
  };

  const handleCardClick = (droneName: string, droneType: string) => {
    if (!dronesData) return;
    const droneData = dronesData.project_drones[droneType]?.[droneName];
    if (!droneData || droneData.dates.length === 0) return;
    // If clicking the already-selected drone, do nothing
    if (selectedDroneName === droneName) return;
    // Load the first date of the clicked drone
    const firstDate = droneData.dates[0];
    handleDateClick(droneName, firstDate, droneType);
  };

  if (isLoading || error || !dronesData) {
    return (
      <BaseLayout 
        fullHeight={true}
        isLoading={isLoading}
        loadingMessage="Loading drone videos..."
        loadingSize="xl"
      >
        {error && (
          <div className="flex items-center justify-center h-screen">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-destructive">Error loading drone data</h2>
              <p className="text-muted-foreground mt-2">Please try again later</p>
            </div>
          </div>
        )}
      </BaseLayout>
    );
  }

  if (dronesData.drone_types.length === 0) {
    return (
      <BaseLayout fullHeight={true}>
        <div className="flex h-[calc(100vh-4rem)] items-center justify-center bg-background px-6">
          <div className="w-full max-w-md rounded-[1.375rem] border border-border bg-card p-8 text-center shadow-slate">
            <div className="mx-auto mb-4 grid size-12 place-items-center rounded-[13px] bg-primary/12 text-primary">
              <Box className="size-6" strokeWidth={1.9} />
            </div>
            <h1 className="font-display text-2xl font-extrabold tracking-[-0.02em] text-foreground">
              No drone videos yet
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Finished 360 tours and 3D Sky Maps for {dronesData.project.name} will appear here.
            </p>
            {dronesData.can_manage_drone_videos && (
              <DroneUploadDialog
                projectId={dronesData.project.id}
                projectName={dronesData.project.name}
                className="mt-5"
              />
            )}
          </div>
        </div>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout fullHeight={true}>
      <div className="h-[calc(100vh-4rem)] w-full bg-background flex overflow-hidden">
        {/* Main Content Area */}
        <div className="flex-1 flex relative">
          {/* Sidebar */}
          <div 
            className={`bg-card border-r border-border flex flex-col transition-all duration-300 ease-in-out h-full ${
              isSidebarCollapsed ? 'w-0' : 'w-80'
            }`}
          >
            <div className={`flex flex-col h-full ${isSidebarCollapsed ? 'hidden' : 'flex'}`}>
              <div className="p-4 border-b border-border flex-shrink-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h1 className="truncate font-display text-xl font-extrabold tracking-[-0.02em] text-foreground">
                      {dronesData.project.name}
                    </h1>
                    <p className="text-sm text-muted-foreground">Drone Tours</p>
                  </div>
                  {dronesData.can_manage_drone_videos && (
                    <DroneUploadDialog
                      projectId={dronesData.project.id}
                      projectName={dronesData.project.name}
                      className="shrink-0 px-3"
                    />
                  )}
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                <Tabs value={selectedDroneType} onValueChange={setSelectedDroneType} className="w-full h-full flex flex-col">
                  <div className="overflow-x-auto mx-4 mt-4 flex-shrink-0">
                    <TabsList
                      className="inline-flex w-auto"
                      data-tour-id="drone-types-tabs"
                    >
                      {dronesData.drone_types.map((droneType) => (
                        <TabsTrigger key={droneType} value={droneType} className="text-xs whitespace-nowrap">
                          {droneType}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </div>

                  {dronesData.drone_types.map((droneType) => (
                    <TabsContent key={droneType} value={droneType} className="mt-4 space-y-2 px-4 pb-4 flex-1 overflow-y-auto">
                      {Object.entries(dronesData.project_drones[droneType]).map(([droneName, droneData]) => (
                        <Card
                          key={droneName}
                          data-tour-id="drone-video-item"
                          className={`cursor-pointer transition-all duration-200 ${
                            selectedDroneName === droneName
                              ? 'bg-primary/12 border-primary/50 ring-1 ring-primary/30'
                              : 'hover:bg-accent/50 hover:border-border'
                          }`}
                          onClick={() => handleCardClick(droneName, droneType)}
                        >
                          <CardContent className="p-3">
                            <div className="mb-2 flex items-center justify-between gap-2">
                              <h4 className="text-sm font-semibold text-foreground">
                                {droneName}
                              </h4>
                              {droneData.videos.some((video) => video.has_sky_map) && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-primary">
                                  <Box className="size-3" strokeWidth={1.9} />3D
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {droneData.dates.map((date, idx) => (
                                <Button
                                  key={`${date}-${idx}`}
                                  size="sm"
                                  variant={selectedDroneName === droneName && selectedDate === date ? 'default' : 'outline'}
                                  className={`text-xs h-6 ${
                                    selectedDroneName === droneName && selectedDate === date
                                      ? 'bg-primary text-primary-foreground hover:brightness-110'
                                      : selectedDroneName === droneName
                                        ? 'border-primary/30 text-foreground hover:bg-primary/15'
                                        : ''
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDateClick(droneName, date, droneType);
                                  }}
                                >
                                  {date}
                                  <ExternalLink className="w-3 h-3 ml-1" />
                                </Button>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            </div>
          </div>

          {/* Toggle Button */}
          <Button
  onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
  className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 h-12 w-10 rounded-r-lg rounded-l-none bg-card border border-border shadow-lg hover:bg-accent transition-all duration-200 flex items-center justify-center"
  style={{
    left: isSidebarCollapsed ? '0px' : '320px',
    transition: 'left 0.3s ease-in-out'
  }}
  variant="outline"
  size="sm"
>
  {isSidebarCollapsed ? (
    <ChevronRight className="h-6 w-6 text-muted-foreground" />
  ) : (
    <ChevronLeft className="h-6 w-6 text-muted-foreground" />
  )}
</Button>




          {/* Main Viewer */}
          <div className="flex flex-1 overflow-hidden">
          {selectedVideo?.has_sky_map ? (
            <FlightPathPanel
              videoId={selectedVideo.id}
              currentTime={currentFlightTime}
              onSeek={seekFlightPath}
              displayMode="viewer"
            />
          ) : (
            <>
              <div className="relative flex-1">
                <iframe
                  ref={viewerFrameRef}
                  src={iframeUrl}
                  className="w-full h-full border-none"
                  allowFullScreen
                  title="Drone Tour Viewer"
                  allow="vr; xr; accelerometer; magnetometer; gyroscope; webvr; webxr; xr-spatial-tracking; autoplay; camera; microphone"
                />
              </div>
              {selectedVideo?.flight_path_url && (
                <FlightPathPanel
                  videoId={selectedVideo.id}
                  currentTime={currentFlightTime}
                  onSeek={seekFlightPath}
                />
              )}
            </>
          )}
          </div>
        </div>
      </div>
    </BaseLayout>
  );
};

export default DroneViewer;
